import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Copy, User, Phone, Mail, MapPin, Truck, Store, StickyNote, CreditCard, Receipt } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Format customizations (dict, string, or empty) into a readable string.
 * e.g. { Sweetness: "50%", Ice: "Less" } -> "Sweetness: 50% • Ice: Less"
 */
export const formatCustomizations = (customizations) => {
  if (!customizations) return null;
  if (typeof customizations === 'string') {
    return customizations.trim() || null;
  }
  if (typeof customizations === 'object') {
    const entries = Object.entries(customizations).filter(([, v]) => v !== null && v !== undefined && `${v}`.trim() !== '');
    if (entries.length === 0) return null;
    return entries.map(([k, v]) => `${k}: ${v}`).join(' • ');
  }
  return String(customizations);
};

const money = (v) => (typeof v === 'number' ? `$${v.toFixed(2)}` : v != null ? `$${Number(v).toFixed(2)}` : '—');

/**
 * Build a plain-English, copy/paste friendly description of ANY order type.
 * Supported order_type values: 'fizze', 'tanning', 'eats', 'unified'
 */
export const buildOrderDescription = (order) => {
  if (!order) return '';
  const lines = [];
  const type = order.order_type || (order.rank_1_name ? 'eats' : order.level_label ? 'tanning' : 'fizze');
  const typeLabel = { fizze: 'FIZZE DRINKS ORDER', tanning: 'TANNING PACKAGE ORDER', eats: '818 EATS ORDER', unified: 'CART ORDER (Tanning + Lotions)' }[type] || 'ORDER';

  lines.push(`===== ${typeLabel} =====`);
  lines.push(`Order #: ${order.order_number || order.order_code || order.order_id || order.id}`);
  if (order.created_at) lines.push(`Placed: ${new Date(order.created_at).toLocaleString()}`);
  lines.push('');
  lines.push('CUSTOMER:');
  lines.push(`  Name: ${order.customer_name || '—'}`);
  if (order.customer_phone) lines.push(`  Phone: ${order.customer_phone}`);
  if (order.customer_email) lines.push(`  Email: ${order.customer_email}`);
  lines.push('');

  // ---- Items ----
  lines.push('WHAT THE CUSTOMER ORDERED:');
  if (type === 'fizze' && Array.isArray(order.items)) {
    order.items.forEach((item, i) => {
      const custom = formatCustomizations(item.customizations);
      lines.push(`  ${i + 1}. ${item.quantity} x ${item.drink_name} — ${money(item.price)} each = ${money(item.quantity * item.price)}`);
      if (custom) lines.push(`     Options: ${custom}`);
    });
  } else if (type === 'tanning') {
    lines.push(`  1 x ${order.package_name || `${order.level_label || ''} - ${order.package_label || ''}`}`);
    if (order.level_label) lines.push(`     Bed: ${order.level_label}`);
    if (order.package_label) lines.push(`     Package: ${order.package_label}`);
    if (order.black_friday_deal) lines.push(`     Promo: BLACK FRIDAY BOGO${order.savings ? ` (customer saved ${money(order.savings)})` : ''}`);
  } else if (type === 'eats') {
    lines.push(`  Quantity: ${order.quantity || 1}${order.item_price ? ` @ ${money(order.item_price)} each` : ''}`);
    if (order.assigned_dish_name) {
      lines.push(`  Assigned Dish: ${order.assigned_dish_name}`);
    }
    if (order.rank_1_name || order.menu_item_name) lines.push(`  1st Choice: ${order.rank_1_name || order.menu_item_name}`);
    if (order.rank_2_name) lines.push(`  2nd Choice: ${order.rank_2_name}`);
    if (order.rank_3_name) lines.push(`  3rd Choice: ${order.rank_3_name}`);
    if (order.delivery_preference) lines.push(`  Preference: ${order.delivery_preference === 'first_available' ? 'First available dish' : '#1 choice only'}`);
    if (order.delivery_week) lines.push(`  Delivery Week: ${order.delivery_week}`);
  } else if (type === 'unified' && Array.isArray(order.items)) {
    order.items.forEach((item, i) => {
      const d = item.details || {};
      const name = item.item_type === 'lotion'
        ? `${d.lotion_brand ? d.lotion_brand + ' ' : ''}${d.lotion_name || 'Lotion'}`
        : `Tanning: ${d.bed_level || ''} ${d.package_type || ''}`.trim();
      lines.push(`  ${i + 1}. ${item.quantity} x ${name} — ${money(item.price)} each = ${money(item.quantity * item.price)}`);
    });
  }
  lines.push('');

  // ---- Fulfillment ----
  const fulfillment = [];
  if (type === 'fizze') {
    fulfillment.push(`  Method: ${order.delivery_method === 'delivery' ? 'DELIVERY' : 'PICKUP at store'}`);
    if (order.delivery_method === 'delivery' && order.delivery_address) fulfillment.push(`  Deliver To: ${order.delivery_address}`);
    if (order.special_instructions) fulfillment.push(`  Special Instructions: ${order.special_instructions}`);
  } else if (type === 'eats') {
    if (order.customer_address) fulfillment.push(`  Deliver To: ${order.customer_address}`);
    if (order.notes) fulfillment.push(`  Notes: ${order.notes}`);
  } else if (type === 'tanning') {
    fulfillment.push('  Redeem in-store. Enter package in Sunlink.');
  }
  if (fulfillment.length) {
    lines.push('FULFILLMENT:');
    lines.push(...fulfillment);
    lines.push('');
  }

  // ---- Pricing ----
  lines.push('PRICE BREAKDOWN:');
  if (order.subtotal != null) lines.push(`  Subtotal: ${money(order.subtotal)}`);
  if (order.sales_tax != null) lines.push(`  Sales Tax: ${money(order.sales_tax)}`);
  if (order.tan_tax != null) lines.push(`  Tan Tax: ${money(order.tan_tax)}`);
  if (order.tax != null) lines.push(`  Tax: ${money(order.tax)}`);
  if (order.delivery_fee != null && order.delivery_fee > 0) lines.push(`  Delivery Fee: ${money(order.delivery_fee)}`);
  if ((order.tip_amount != null && order.tip_amount > 0) || (order.tip != null && order.tip > 0)) lines.push(`  Tip: ${money(order.tip_amount ?? order.tip)}`);
  lines.push(`  TOTAL: ${money(order.total)}`);
  lines.push('');

  // ---- Payment ----
  lines.push('PAYMENT:');
  const paidOnline = order.paid || order.payment_status === 'paid' || (order.payment_method && order.payment_method !== 'cash_on_delivery');
  if (paidOnline) {
    lines.push(`  Status: PAID${order.payment_method ? ` via ${order.payment_method}` : ''}`);
    if (order.paid_at) lines.push(`  Paid At: ${new Date(order.paid_at).toLocaleString()}`);
    if (order.coupon_code) lines.push(`  Coupon Code: ${order.coupon_code}`);
    if (order.paypal_order_id) lines.push(`  PayPal Order ID: ${order.paypal_order_id}`);
  } else {
    lines.push(`  Status: NOT PAID ONLINE${type === 'fizze' ? ' (collect cash at pickup/delivery)' : ''}`);
  }
  if (order.status) lines.push(`  Order Status: ${String(order.status).replace(/_/g, ' ')}`);

  return lines.join('\n');
};

const InfoRow = ({ icon: Icon, label, value, testId }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-sm" data-testid={testId}>
      <Icon className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
      <span className="font-semibold text-foreground min-w-[90px]">{label}:</span>
      <span className="text-foreground break-words">{value}</span>
    </div>
  );
};

/**
 * Full order details dialog. Works for fizze, tanning, eats & unified orders.
 * Props: order (with order_type), onClose(), onDownloadReceipt(order) optional
 */
export const OrderDetailsDialog = ({ order, onClose, onDownloadReceipt }) => {
  if (!order) return null;
  const type = order.order_type || (order.rank_1_name ? 'eats' : order.level_label ? 'tanning' : 'fizze');
  const typeMeta = {
    fizze: { emoji: '🥤', label: 'Fizze Drinks Order', color: 'bg-teal-600' },
    tanning: { emoji: '☀️', label: 'Tanning Package Order', color: 'bg-orange-600' },
    eats: { emoji: '🍲', label: '818 EATS Order', color: 'bg-amber-600' },
    unified: { emoji: '🛒', label: 'Cart Order', color: 'bg-purple-600' }
  }[type];

  const paidOnline = order.paid || order.payment_status === 'paid' || (order.payment_method && order.payment_method !== 'cash_on_delivery');

  const handleCopy = async () => {
    const text = buildOrderDescription(order);
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Order details copied to clipboard');
    } catch (e) {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success('Order details copied to clipboard');
    }
  };

  return (
    <Dialog open={!!order} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="order-details-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <span className="text-2xl">{typeMeta.emoji}</span>
            {typeMeta.label}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Order header */}
          <div className="flex items-center justify-between">
            <div>
              <div className="font-mono font-bold text-lg" data-testid="details-order-number">
                {order.order_number || order.order_code}
              </div>
              <div className="text-xs text-muted-foreground">
                {order.created_at && new Date(order.created_at).toLocaleString()}
              </div>
            </div>
            {paidOnline ? (
              <Badge className="bg-green-600 text-white" data-testid="details-payment-badge">
                ✅ PAID {order.payment_method ? `via ${order.payment_method}` : ''}
              </Badge>
            ) : (
              <Badge className="bg-red-600 text-white" data-testid="details-payment-badge">
                {type === 'fizze' ? '💵 Cash on pickup/delivery' : '❌ Not Paid'}
              </Badge>
            )}
          </div>

          <Separator />

          {/* Customer */}
          <div className="space-y-1.5" data-testid="details-customer-section">
            <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Customer</div>
            <InfoRow icon={User} label="Name" value={order.customer_name} testId="details-customer-name" />
            <InfoRow icon={Phone} label="Phone" value={order.customer_phone} testId="details-customer-phone" />
            <InfoRow icon={Mail} label="Email" value={order.customer_email} testId="details-customer-email" />
          </div>

          <Separator />

          {/* Items */}
          <div className="space-y-2" data-testid="details-items-section">
            <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">What They Ordered</div>

            {type === 'fizze' && order.items?.map((item, i) => {
              const custom = formatCustomizations(item.customizations);
              return (
                <div key={i} className="bg-teal-50 border border-teal-200 rounded-lg p-3" data-testid={`details-item-${i}`}>
                  <div className="flex justify-between items-start">
                    <div className="font-bold text-teal-900">
                      {item.quantity} × {item.drink_name}
                    </div>
                    <div className="text-sm font-semibold text-teal-800">{money(item.quantity * item.price)}</div>
                  </div>
                  {custom && (
                    <div className="text-xs text-teal-700 mt-1 bg-white rounded px-2 py-1">
                      <span className="font-semibold">Options:</span> {custom}
                    </div>
                  )}
                  <div className="text-xs text-teal-600 mt-1">{money(item.price)} each</div>
                </div>
              );
            })}

            {type === 'tanning' && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3" data-testid="details-tanning-package">
                <div className="font-bold text-orange-900 text-base">
                  {order.package_name || `${order.level_label || ''} — ${order.package_label || ''}`}
                </div>
                <div className="text-sm text-orange-800 mt-1 space-y-0.5">
                  {order.level_label && <div><span className="font-semibold">Bed:</span> {order.level_label}</div>}
                  {order.package_label && <div><span className="font-semibold">Package:</span> {order.package_label}</div>}
                </div>
                {order.black_friday_deal && (
                  <Badge className="bg-yellow-500 text-black text-xs mt-2">
                    ⚡ BLACK FRIDAY BOGO {order.savings ? `— saved ${money(order.savings)}` : ''}
                  </Badge>
                )}
              </div>
            )}

            {type === 'eats' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1.5" data-testid="details-eats-order">
                <div className="font-bold text-amber-900">
                  Quantity: {order.quantity || 1}{order.item_price ? ` @ ${money(order.item_price)} each` : ''}
                </div>
                {order.assigned_dish_name && (
                  <div className="text-sm"><span className="font-semibold">Assigned Dish:</span> {order.assigned_dish_name}</div>
                )}
                <div className="text-sm space-y-0.5">
                  {(order.rank_1_name || order.menu_item_name) && <div>🥇 1st Choice: <strong>{order.rank_1_name || order.menu_item_name}</strong></div>}
                  {order.rank_2_name && <div>🥈 2nd Choice: {order.rank_2_name}</div>}
                  {order.rank_3_name && <div>🥉 3rd Choice: {order.rank_3_name}</div>}
                </div>
                {order.delivery_preference && (
                  <div className="text-xs text-amber-800">
                    Preference: {order.delivery_preference === 'first_available' ? 'First available dish' : '#1 choice only'}
                  </div>
                )}
                {order.delivery_week && <div className="text-xs text-amber-800">Delivery Week: {order.delivery_week}</div>}
              </div>
            )}

            {type === 'unified' && order.items?.map((item, i) => {
              const d = item.details || {};
              const name = item.item_type === 'lotion'
                ? `${d.lotion_brand ? d.lotion_brand + ' ' : ''}${d.lotion_name || 'Lotion'}`
                : `Tanning: ${d.bed_level || ''} ${d.package_type || ''}`.trim();
              return (
                <div key={i} className="bg-purple-50 border border-purple-200 rounded-lg p-3" data-testid={`details-item-${i}`}>
                  <div className="flex justify-between">
                    <div className="font-bold text-purple-900">{item.quantity} × {name}</div>
                    <div className="text-sm font-semibold">{money(item.quantity * item.price)}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Fulfillment */}
          {(type === 'fizze' || type === 'eats') && (
            <>
              <Separator />
              <div className="space-y-1.5" data-testid="details-fulfillment-section">
                <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Fulfillment</div>
                {type === 'fizze' && (
                  <InfoRow
                    icon={order.delivery_method === 'delivery' ? Truck : Store}
                    label="Method"
                    value={order.delivery_method === 'delivery' ? 'DELIVERY' : 'PICKUP at store'}
                    testId="details-delivery-method"
                  />
                )}
                <InfoRow icon={MapPin} label="Address" value={order.delivery_address || order.customer_address} testId="details-delivery-address" />
                <InfoRow icon={StickyNote} label="Instructions" value={order.special_instructions || order.notes} testId="details-special-instructions" />
              </div>
            </>
          )}

          <Separator />

          {/* Price breakdown */}
          <div className="space-y-1 text-sm" data-testid="details-price-section">
            <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1">Price Breakdown</div>
            {order.subtotal != null && <div className="flex justify-between"><span>Subtotal</span><span>{money(order.subtotal)}</span></div>}
            {order.sales_tax != null && <div className="flex justify-between"><span>Sales Tax</span><span>{money(order.sales_tax)}</span></div>}
            {order.tan_tax != null && <div className="flex justify-between"><span>Tan Tax</span><span>{money(order.tan_tax)}</span></div>}
            {order.tax != null && <div className="flex justify-between"><span>Tax</span><span>{money(order.tax)}</span></div>}
            {order.delivery_fee != null && order.delivery_fee > 0 && <div className="flex justify-between"><span>Delivery Fee</span><span>{money(order.delivery_fee)}</span></div>}
            {((order.tip_amount ?? order.tip) || 0) > 0 && <div className="flex justify-between"><span>Tip</span><span>{money(order.tip_amount ?? order.tip)}</span></div>}
            <Separator className="my-1" />
            <div className="flex justify-between font-bold text-base" data-testid="details-total">
              <span>TOTAL</span>
              <span className="text-green-600">{money(order.total)}</span>
            </div>
          </div>

          {/* Payment details */}
          {(order.paid_at || order.paypal_order_id) && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs space-y-1" data-testid="details-payment-info">
              <div className="flex items-center gap-1.5 font-semibold text-green-900"><CreditCard className="w-3.5 h-3.5" /> Payment Info</div>
              {order.paid_at && <div>Paid at: {new Date(order.paid_at).toLocaleString()}</div>}
              {order.paypal_order_id && <div>PayPal ID: <span className="font-mono">{order.paypal_order_id}</span></div>}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={handleCopy} className="flex-1" data-testid="details-copy-button">
            <Copy className="w-4 h-4 mr-1.5" /> Copy Details
          </Button>
          {onDownloadReceipt && (type === 'fizze' || type === 'tanning') && (
            <Button variant="outline" onClick={() => onDownloadReceipt(order)} className="flex-1" data-testid="details-receipt-button">
              <Receipt className="w-4 h-4 mr-1.5" /> Receipt
            </Button>
          )}
          <Button onClick={onClose} className="flex-1" data-testid="details-close-button">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
